// @sphere/auth/constants/roles.constants.ts

/**
 * Rôles métier de l'application Sphere.
 *
 * ✅ Principes :
 * - Rôles FONCTIONNELS, pas techniques
 * - Correspondent à des responsabilités métier claires
 * - Alignés avec les rôles Keycloak
 *
 * @example
 * ```typescript
 * // Dans une policy
 * if (hasRole(ctx, UserRole.TEACHER)) {
 *   // Logique spécifique aux enseignants
 * }
 * ```
 */
export enum UserRole {
  // ========== Rôles Administratifs ==========

  /**
   * Administrateur système global.
   * Accès complet à toutes les fonctionnalités, tous les établissements.
   */
  ADMIN = 'admin',

  /**
   * Directeur/Principal d'établissement.
   * Gestion complète de son établissement (classes, enseignants, contenu).
   */
  PRINCIPAL = 'principal',

  /**
   * Coordinateur pédagogique.
   * Supervise et valide le contenu pédagogique de son établissement.
   */
  PEDAGOGICAL_COORDINATOR = 'pedagogical_coordinator',

  // ========== Rôles Pédagogiques ==========

  /**
   * Enseignant.
   * Crée et gère du contenu pédagogique, enseigne des classes/groupes.
   */
  TEACHER = 'teacher',

  /**
   * Étudiant.
   * Consulte le contenu pédagogique, effectue des tentatives d'évaluation.
   */
  STUDENT = 'student',

  // ========== Rôles Administratifs (Support) ==========

  /**
   * Gestionnaire administratif.
   * Gère les inscriptions, les classes, sans accès au contenu pédagogique.
   */
  ADMINISTRATIVE_MANAGER = 'administrative_manager',

  /**
   * Support technique.
   * Assiste les utilisateurs, accès en lecture seule pour le diagnostic.
   */
  SUPPORT = 'support',
}

// @sphere/auth/constants/roles.constants.ts

/**
 * Groupes de rôles pour faciliter les vérifications d'autorisation.
 *
 * ✅ Principes :
 * - Groupes par CAPACITÉ MÉTIER, pas par ressource
 * - Nommage clair et explicite (verbe d'action)
 * - Réutilisables dans toutes les policies
 *
 * @example
 * ```typescript
 * // Dans une policy
 * if (hasAnyRole(ctx, RoleGroups.CAN_MANAGE_PEDAGOGICAL_CONTENT)) {
 *   return { allowed: true }
 * }
 * ```
 */
export const RoleGroups = {
  // ========== Administration Système ==========

  /**
   * Rôles avec accès administrateur global (tous établissements).
   */
  SYSTEM_ADMINS: [UserRole.ADMIN] as const,

  /**
   * Rôles pouvant administrer un établissement.
   * Gestion des classes, enseignants, inscriptions dans leur périmètre.
   */
  CAN_ADMINISTER_ESTABLISHMENT: [UserRole.ADMIN, UserRole.PRINCIPAL] as const,

  // ========== Gestion du Contenu Pédagogique ==========

  /**
   * Rôles pouvant CRÉER du contenu pédagogique (chapitres, leçons).
   * Limité à leur périmètre (établissement/enseignement).
   */
  CAN_CREATE_PEDAGOGICAL_CONTENT: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
    UserRole.TEACHER,
  ] as const,

  /**
   * Rôles pouvant MODIFIER tout contenu pédagogique de leur établissement.
   * Pas seulement leur propre contenu.
   */
  CAN_MODERATE_PEDAGOGICAL_CONTENT: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
  ] as const,

  /**
   * Rôles pouvant CONSULTER le contenu pédagogique.
   */
  CAN_VIEW_PEDAGOGICAL_CONTENT: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
    UserRole.TEACHER,
    UserRole.STUDENT,
    UserRole.SUPPORT,
  ] as const,

  // ========== Gestion Organisationnelle ==========

  /**
   * Rôles pouvant créer et gérer des établissements.
   */
  CAN_MANAGE_ESTABLISHMENTS: [UserRole.ADMIN] as const,

  /**
   * Rôles pouvant créer et gérer des classes.
   */
  CAN_MANAGE_CLASSES: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.ADMINISTRATIVE_MANAGER,
  ] as const,

  /**
   * Rôles pouvant gérer les inscriptions (élèves → classes).
   */
  CAN_MANAGE_ENROLLMENTS: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.ADMINISTRATIVE_MANAGER,
  ] as const,

  /**
   * Rôles pouvant CONSULTER la structure organisationnelle :
   * établissements, classes, effectifs, enseignements, référentiels.
   * Inclut ADMINISTRATIVE_MANAGER (contrairement à CAN_VIEW_PEDAGOGICAL_CONTENT),
   * qui gère l'administratif sans accès au contenu pédagogique.
   */
  CAN_VIEW_ORGANIZATIONAL_STRUCTURE: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
    UserRole.TEACHER,
    UserRole.STUDENT,
    UserRole.ADMINISTRATIVE_MANAGER,
    UserRole.SUPPORT,
  ] as const,

  // ========== Gestion des Utilisateurs (Membres) ==========

  /**
   * Rôles pouvant CONSULTER les fiches utilisateurs (membres) de leur
   * établissement. Inclut SUPPORT en lecture seule (diagnostic).
   * Aligné avec `canViewUser()` dans sphere-user-service/security.util.
   */
  CAN_VIEW_USERS: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.ADMINISTRATIVE_MANAGER,
    UserRole.SUPPORT,
  ] as const,

  /**
   * Rôles pouvant GÉRER les utilisateurs (créer, modifier, suspendre,
   * désactiver) de leur établissement.
   * Aligné avec `canManageUsers()` dans sphere-user-service/security.util.
   */
  CAN_MANAGE_USERS: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.ADMINISTRATIVE_MANAGER,
  ] as const,

  /**
   * Rôles pouvant affecter des enseignants aux enseignements.
   */
  CAN_MANAGE_TEACHING_STAFF: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
  ] as const,

  // ========== Enseignement ==========

  /**
   * Rôles pouvant enseigner (affectés comme staff aux enseignements).
   */
  CAN_TEACH: [UserRole.TEACHER, UserRole.PEDAGOGICAL_COORDINATOR] as const,

  /**
   * Rôles pouvant créer et gérer des enseignements (teachings).
   */
  CAN_MANAGE_TEACHINGS: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
  ] as const,

  // ========== Évaluation ==========

  /**
   * Rôles pouvant noter des tentatives d'évaluation.
   */
  CAN_GRADE_ATTEMPTS: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
    UserRole.TEACHER,
  ] as const,

  /**
   * Rôles pouvant effectuer des tentatives d'évaluation.
   */
  CAN_SUBMIT_ATTEMPTS: [
    UserRole.STUDENT,
    UserRole.TEACHER, // Pour tester
  ] as const,

  // ========== Référentiels ==========

  /**
   * Rôles pouvant gérer les référentiels (matières, niveaux, statuts).
   */
  CAN_MANAGE_REFERENCES: [UserRole.ADMIN] as const,

  /**
   * Rôles pouvant créer des templates de contenu.
   */
  CAN_CREATE_TEMPLATES: [
    UserRole.ADMIN,
    UserRole.PEDAGOGICAL_COORDINATOR,
    UserRole.TEACHER,
  ] as const,

  // ========== Support ==========

  /**
   * Rôles avec accès en lecture seule (diagnostic, support).
   */
  READ_ONLY_ACCESS: [UserRole.SUPPORT] as const,

  // ========== Groupes Génériques ==========

  /**
   * Tous les utilisateurs authentifiés.
   */
  ALL_AUTHENTICATED: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
    UserRole.TEACHER,
    UserRole.STUDENT,
    UserRole.ADMINISTRATIVE_MANAGER,
    UserRole.SUPPORT,
  ] as const,

  // ========== Alias pour Compatibilité ==========

  /**
   * @deprecated Utiliser SYSTEM_ADMINS à la place
   */
  ADMIN_ONLY: [UserRole.ADMIN] as const,

  /**
   * @deprecated Utiliser CAN_VIEW_PEDAGOGICAL_CONTENT à la place
   */
  VIEWERS: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
    UserRole.TEACHER,
    UserRole.STUDENT,
    UserRole.SUPPORT,
  ] as const,

  /**
   * @deprecated Utiliser CAN_CREATE_PEDAGOGICAL_CONTENT à la place
   */
  CONTENT_CREATORS: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
    UserRole.TEACHER,
  ] as const,

  /**
   * @deprecated Utiliser ALL_AUTHENTICATED à la place
   */
  ALL_USERS: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.PEDAGOGICAL_COORDINATOR,
    UserRole.TEACHER,
    UserRole.STUDENT,
    UserRole.ADMINISTRATIVE_MANAGER,
    UserRole.SUPPORT,
  ] as const,

  /**
   * @deprecated Utiliser CAN_ADMINISTER_ESTABLISHMENT à la place
   */
  ESTABLISHMENT_MANAGERS: [UserRole.ADMIN, UserRole.PRINCIPAL] as const,

  /**
   * @deprecated Utiliser CAN_MANAGE_CLASSES à la place
   */
  CLASS_MANAGERS: [
    UserRole.ADMIN,
    UserRole.PRINCIPAL,
    UserRole.ADMINISTRATIVE_MANAGER,
  ] as const,
} as const

/**
 * Type helper pour extraire les valeurs des groupes de rôles.
 */
export type RoleGroup = (typeof RoleGroups)[keyof typeof RoleGroups]
